import React from 'react';
import { observer } from 'mobx-react-lite';
import WorklistDataGridStore from '@/Pages/GenericWorklist/Components/DataGrid/WorklistDataGridStore';
import { DobIcon, DobLabel, DobTableWrapper, DobTextarea } from '@dedalusdiit/dob-components';
import { t } from 'i18next';
import Styles from '@/Pages/GenericWorklist/Components/RemarksPopup.module.scss';

interface RemarksPopupProps {
    dataGridStore: WorklistDataGridStore;
}

const renderAllergies = (dataGridStore: WorklistDataGridStore): React.ReactElement => {
    const allergies = dataGridStore.selectedOrderRemarks?.PatientRemark?.Allergies;

    if (!allergies || allergies.length === 0) {
        return <div className={Styles.emptyValue}>-</div>;
    }

    return (
        <>
            {allergies.map((allergy) => (
                <div key={allergy.Id} className={Styles.allergy}>
                    {allergy.Important && <DobIcon className={Styles.warningIcn} size="14px" name="exclamation-triangle" />}
                    {allergy.Text}
                </div>
            ))}
        </>
    );
};

const renderParticularities = (dataGridStore: WorklistDataGridStore): React.ReactElement => {
    const particularities = dataGridStore.selectedOrderRemarks?.PatientRemark?.Particularities;

    if (!particularities || particularities.length === 0) {
        return <div className={Styles.emptyValue}>-</div>;
    }

    return (
        <DobTableWrapper>
            <table>
                {particularities.map((particularity) => (
                    <tr key={particularity.Id}>
                        <td>
                            <b>{particularity.Type}: </b>
                            {particularity.Value}
                        </td>
                        <td>{particularity.Remarks}</td>
                        <td>
                            {particularity.DateTimeFrom} - {particularity.DateTimeTo}
                        </td>
                    </tr>
                ))}
            </table>
        </DobTableWrapper>
    );
};

const RemarksPopup = observer(({ dataGridStore }: Readonly<RemarksPopupProps>): React.ReactElement => {
    return (
        <div
            className={Styles.remarksPopup}
            style={{ display: dataGridStore.showRemarksModalIsOpen ? 'block' : 'none' }}
        >
            <div className={Styles.remarksHeader}>{t('Remarks')}</div>

            <div className={Styles.content}>
                <div className={Styles.remarksSection}>
                    <div className={Styles.sectionTitle}>{t('Patient')} - {dataGridStore.selectedOrderRemarks?.PatientRemark?.FirstName} {dataGridStore.selectedOrderRemarks?.PatientRemark?.LastName}</div>
                    <div className={Styles.inputRow}>
                        <div className={Styles.inputGroup}>
                            <DobLabel text={t('Allergies')} />
                            <div className={Styles.multilineTextInput}>
                                <div className={Styles.allergies}>
                                    {renderAllergies(dataGridStore)}
                                </div>
                            </div>
                        </div>
                        <div className={Styles.inputGroup}>
                            <DobTextarea direction="vertical" label={t('PublicRemarks')} value={dataGridStore.selectedOrderRemarks?.PatientRemark?.Public} readOnly={true} />
                        </div>
                        <div className={Styles.inputGroup}>
                            <DobTextarea direction="vertical" label={t('PrivateRemarks')} value={dataGridStore.selectedOrderRemarks?.PatientRemark?.Private} readOnly={true} />
                        </div>
                    </div>

                    <div className={`${Styles.inputGroup}`}>
                        <DobLabel text={t('PatientParticularities')} />
                        <div className={Styles.multilineTextInput}>
                            { renderParticularities(dataGridStore) }
                        </div>
                    </div>
                </div>

                <div className={Styles.remarksSection}>
                    <div className={Styles.sectionTitle}>{ t('Order') } - #{ dataGridStore.selectedOrderRemarks?.OrderNumber }</div>
                    <div className={`${Styles.inputGroup}`}>
                        <DobTextarea direction="vertical" label={t('Remarks')} value={dataGridStore.selectedOrderRemarks?.OrderRemarks} readOnly={true} />
                    </div>
                </div>

                <div className={Styles.remarksSection}>
                    <div className={Styles.sectionTitle}>{ t('Applicant') } - { dataGridStore.selectedOrderRemarks?.RequestingName }</div>
                    <div className={Styles.inputRow}>
                        <div className={Styles.inputGroup}>
                            <DobTextarea direction="vertical" label={t('PublicRemarks')} value={dataGridStore.selectedOrderRemarks?.RequestingPublic} readOnly={true} />
                        </div>
                        <div className={Styles.inputGroup}>
                            <DobTextarea direction="vertical" label={t('PrivateRemarks')} value={dataGridStore.selectedOrderRemarks?.RequestingPrivate} readOnly={true} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default RemarksPopup;
